import { LinkedinMoment, LinkedinTemplate, ProspectSignal, ProspectState, SignalKey } from "../../interfaces/linkedinProspecting";

/** The Signaux filter: a fresh opening, or nothing moving. One or the other, never both. */
export const SIGNAL_FILTERS: Array<{ key: SignalKey; label: string }> = [
    { key: "opening", label: "Ouverture récente" },
    { key: "none", label: "Aucun" },
];

/** "il y a 3 j" for the past. */
export function relativeFr(iso: string): string {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 14) return `il y a ${days} j`;
    if (days < 60) return `il y a ${Math.floor(days / 7)} sem`;
    return `il y a ${Math.floor(days / 30)} mois`;
}

/**
 * A follow-up date as the rep reads it: overdue first, then today / tomorrow, then the date.
 * `overdue` lets the row colour it.
 */
export function dueLabel(iso: string): { text: string; overdue: boolean; today: boolean } {
    const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diff = Math.round((day(new Date(iso)) - day(new Date())) / 86_400_000);
    if (diff < 0) return { text: diff === -1 ? "En retard d'un jour" : `En retard de ${-diff} jours`, overdue: true, today: false };
    if (diff === 0) return { text: "Aujourd'hui", overdue: false, today: true };
    if (diff === 1) return { text: "Demain", overdue: false, today: false };
    return {
        text: new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }),
        overdue: false,
        today: false,
    };
}

/** Follow-up presets, in days from today. */
export const FOLLOW_UP_PRESETS: Array<{ label: string; days: number }> = [
    { label: "Demain", days: 1 },
    { label: "Dans 3 jours", days: 3 },
    { label: "Dans 1 semaine", days: 7 },
    { label: "Dans 2 semaines", days: 14 },
    { label: "Dans 1 mois", days: 30 },
];

/** 9:00 local, `days` from today — a follow-up is a day, the hour only keeps it off midnight. */
export function followUpDate(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
}

const titleCase = (word: string) =>
    word.toLowerCase().replace(/(^|[-\s])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase());

/**
 * Registry names often come surname-first in capitals ("DUPONT Jean"); LinkedIn ones
 * first-name-first. The first word that isn't all caps is the first name; otherwise the first.
 */
export function firstName(fullName: string): string {
    const words = fullName.trim().split(/\s+/);
    const given = words.find((w) => /\p{Ll}/u.test(w)) || words[0] || "";
    return titleCase(given);
}

/** LinkedIn's limit for an invitation note. */
export const INVITE_NOTE_LIMIT = 300;

/** The variable a note carries for the rep's own pitch, set in Mon compte. */
export const OFFER_TOKEN = "{votre offre}";

/** Used until the rep writes their own — generic on purpose, it has to fit any supplier. */
export const DEFAULT_OFFER = "J'accompagne les restaurateurs de la région sur leurs approvisionnements.";

/** Which default template fits the prospect: their freshest signal, else their portfolio size. */
export function angleFor(signal: ProspectSignal | null | undefined, sites: number): string {
    if (signal?.type === "restaurant_opening") return "opening";
    if (signal?.type === "restaurant_owner_change") return "owner_change";
    if (signal && ["multi_site", "network_expansion", "franchise_signing"].includes(signal.type)) return "new_site";
    return sites > 1 ? "portfolio" : "generic";
}

/** The messages once connected, in the order a conversation goes. */
export const MESSAGE_MOMENTS: Array<{ key: Exclude<LinkedinMoment, "invitation">; label: string; hint: string }> = [
    { key: "first_message", label: "Premier message", hint: "Juste après l'acceptation" },
    { key: "followup", label: "Relance", hint: "Pas de réponse à votre message" },
    { key: "meeting_ask", label: "Proposer un échange", hint: "Il a répondu" },
    { key: "after_meeting", label: "Après le RDV", hint: "Le suivi du rendez-vous" },
];

export const momentOf = (t: LinkedinTemplate): LinkedinMoment => t.linkedinMoment || "invitation";

/** The message the prospect's stage calls for, or null while they can't be written to yet. */
export function momentFor(state: ProspectState): Exclude<LinkedinMoment, "invitation"> | null {
    const inv = state.invite;
    if (!inv?.acceptedAt && !inv?.repliedAt) return null;
    if (state.customer || inv.meetingAt) return "after_meeting";
    if (inv.repliedAt) return "meeting_ask";
    if (inv.messagedAt) return "followup";
    return "first_message";
}
